import getMenuItems from 'util/get-menu-items';

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CalenderIcon, ChatIcon, CloseXIcon, VideoIcon } from 'assets/svg-icons';
import WidgetType from 'const/widget-type-constant';
import { useCall } from 'context/call-context';
import { useStoreDetails } from 'context/store-context';
import { checkColorBrightness, getContrastingColor } from 'helper/check-color-brightness';
import { getCustomButtonStyle } from 'helper/get-custom-button-style';
import { isHttpsPresent } from 'helper/is-https-present';
import { renderPrimaryIconBaseOnBaseUrl } from 'helper/popup-chat/popup-chat-helper';
import { Action, Index } from 'hooks/models';
import useActivityLogger from 'hooks/useActivityLogger';
import useScreenSize from 'hooks/useScreenSize';
import useWidgetPosition from 'hooks/useWidgetPosition';
import { useTranslation } from 'react-i18next';
import type { MenuOption, OtherWidget } from 'types';
import { ButtonShape, MenuType, OtherWidgetType } from 'types';

import classes from './chat-bubble.module.scss';

type ChatBubbleProps = {
  chatEnabled: boolean;
  isAgentsOnline: boolean;
  onBubbleButtonClick: (value: boolean) => void;
  onAppointmentClick: () => void;
};

const ChatBubble: React.FC<ChatBubbleProps> = ({
  chatEnabled,
  isAgentsOnline,
  onBubbleButtonClick,
  onAppointmentClick
  // eslint-disable-next-line sonarjs/cognitive-complexity
}): JSX.Element => {
  const { widgetOption, onlineMenu, offlineMenu, conciergeUrl, otherWidgets, schedulingOption, theme, callQueuing } =
    useStoreDetails();
  const { avatarImg, type, texts, color, collapsed, collapsedOnMobile, buttonShape, widgetType } = widgetOption;
  const isChatEnabled = chatEnabled || (callQueuing && isAgentsOnline);
  const isOnline: string = isChatEnabled ? '#8DBC67' : '#6f6f6f';
  const textColor = getContrastingColor(theme?.textColor || '#ffffff');
  const { isMobile } = useScreenSize();
  const isLeftPosiotion = useWidgetPosition();
  const isBubbleOrOpenCTA =
    type === WidgetType.BUBBLE || type === WidgetType.OPEN_CTA || widgetType.desktop === WidgetType.BUBBLE;
  const widgetOptionType = isBubbleOrOpenCTA || type === WidgetType.SEMI_OPEN_CTA;
  const primaryTextColor = checkColorBrightness({
    backgroundColor: color || '#ffffff',
    textColor: theme.textColor ?? '#000000'
  });
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [onlineBtn, setOnlineBtn] = useState<boolean>(true);
  const [offlineBtn, setOfflineBtn] = useState<boolean>(true);
  const [showWidget, setShowWidget] = useState<boolean>(!!sessionStorage.getItem('isWidgetShow'));
  const otherWidgetsRef = useRef<Map<string, boolean>>(new Map());
  const { t } = useTranslation();
  const { log } = useActivityLogger();
  const { isActiveLastSession } = useCall();
  const customStyle = getCustomButtonStyle(buttonShape, 'black');
  const fontWeightValue = buttonShape === ButtonShape.SQUARE ? 400 : 700;

  const hideOtherWidget = (id: string) => {
    const otherWidget = document.querySelector(`#${id}, .${id}`);
    if (otherWidget && otherWidget instanceof HTMLElement) {
      otherWidgetsRef.current.set(id, false);
      otherWidget.style.display = 'none';
      otherWidget.style.visibility = 'hidden';
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      otherWidgets.forEach(({ id, type, function: otherFunction }) => {
        if (type === OtherWidgetType.FUNCTION && otherFunction) {
          try {
            const otherWidgetFunction = new Function(otherFunction);
            otherWidgetFunction();
            otherWidgetsRef.current.set(otherFunction, false);
          } catch (error) {
            void log({
              action: Action.ERROR,
              description: `other widget function, error message: ${(error as Error).message}`,
              index: Index.ACTIVITY,
              label: 'ERROR'
            });
            console.error('other widget function: ', error);
          }
          return;
        }
        if (!id) return;
        hideOtherWidget(id);
      });
      if (otherWidgetsRef.current.size === otherWidgets.length) {
        clearInterval(interval);
      }
    }, 1000);
  }, [otherWidgets]);

  useEffect(() => {
    if ((isMobile && collapsedOnMobile) || (!isMobile && collapsed)) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  }, [isMobile]);

  const onClickAvatar = () => {
    if (chatEnabled) {
      onBubbleButtonClick(true);
    } else {
      if (conciergeUrl) {
        window.location.href = isHttpsPresent(conciergeUrl) ? conciergeUrl : 'https://' + conciergeUrl;
      }
    }
    setIsCollapsed(true);
  };

  const onClose = () => {
    log({
      action: Action.WIDGET_CLOSED_BY_CUSTOMER,
      description: 'close widget by customer',
      label: 'CUSTOMER_WIDGET'
    });
    sessionStorage.setItem('isWidgetShow', 'true');
    setShowWidget(true);
  };

  const onOpen = () => {
    log({
      action: Action.WIDGET_OPENED_BY_CUSTOMER,
      description: 'widget opened by customer',
      label: 'CUSTOMER_WIDGET'
    });
    setIsCollapsed(false);
  };

  const onPressOfflineLink = () => {
    log({
      action: Action.OFFLINE_LINK_CLICKED_BY_CUSTOMER,
      description: 'customer click offline menu',
      label: 'CUSTOMER_WIDGET'
    });
  };
  // This function suppress other widgets and it works as toggle
  const toggleOtherWidget = (otherWidget: OtherWidget) => {
    const { id, function: otherFunction } = otherWidget;
    if (otherWidget.type === OtherWidgetType.FUNCTION && otherFunction) {
      try {
        const otherWidgetFunction = new Function(otherFunction);
        otherWidgetFunction();
        otherWidgetsRef.current.set(otherFunction, false);
      } catch (error) {
        void log({
          action: Action.ERROR,
          description: `other widget function, error message: ${(error as Error).message}`,
          index: Index.ACTIVITY,
          label: 'ERROR'
        });
        console.error('other widget function: ', error);
      }
      return;
    }
    if (!id) return;
    const currentElement = document.querySelector(`#${id}, .${id}`);

    if (currentElement && currentElement instanceof HTMLElement) {
      const shouldSuppress = currentElement.style.display !== 'none';
      otherWidgetsRef.current.set(id, shouldSuppress);
      currentElement.style.display = shouldSuppress ? 'none' : 'revert';
      currentElement.style.visibility = shouldSuppress ? 'hidden' : 'revert';
    }
  };

  const renderOtherWidget = useCallback(
    () =>
      otherWidgets &&
      otherWidgets
        .filter(
          (otherWidget) =>
            otherWidget?.label?.trim() &&
            ((otherWidget.id && otherWidgetsRef.current.get(otherWidget.id) !== undefined) ||
              (otherWidget.function && otherWidgetsRef.current.get(otherWidget.function) !== undefined))
        )
        .map((otherWidget) => (
          <button
            aria-label={`Chat button`}
            key={otherWidget.id}
            onClick={() => toggleOtherWidget(otherWidget)}
            style={{
              border: `2px solid ${color}`,
              color: textColor,
              display: otherWidget?.label?.trim() === '' || !otherWidget?.label ? 'none' : 'flex',
              fontFamily: theme?.font,
              ...customStyle
            }}
            className={classes.cwSecondaryBtn}
          >
            <ChatIcon
              style={{ marginRight: '10px' }}
              color={textColor}
            />
            {widgetOptionType && (
              <span
                style={{ fontWeight: fontWeightValue }}
                className={classes.cwBtnText}
              >
                {otherWidget.label}
              </span>
            )}
          </button>
        )),
    [otherWidgetsRef, otherWidgets]
  );

  return (
    <>
      <div
        style={showWidget ? { display: 'none' } : {}}
        className={classes.cwChatBubble}
      >
        <div className={isLeftPosiotion ? classes.cwLeft : classes.cwRight}>
          {/* Image container */}
          <div className={`${classes.cwImageConatiner} ${isChatEnabled ? classes.cwImageConatinerOnline : ''}`}>
            <div
              role="button"
              aria-label={`plus button`}
              onClick={onOpen}
              className={classes.cwPlusBtnContainer}
              style={{
                backgroundColor: color,
                display: isCollapsed ? 'flex' : 'none'
              }}
            >
              <span className={classes.cwPlusBtn}>+</span>
            </div>

            <div className={classes.cwOnlineStatus}>
              <span
                className={classes.cwOnlineStatusDot}
                style={{ backgroundColor: isOnline }}
              />
            </div>
            <div onClick={onClickAvatar}>
              <img
                loading="lazy"
                className={avatarImg ? classes.cwAgentImage : classes.cwDefaultImage}
                src={avatarImg ?? 'https://conferwithuat.s3.eu-west-2.amazonaws.com/loop.gif'}
                alt={avatarImg ?? 'conferWith default image'}
              />
            </div>
          </div>

          {/* Body */}
          {!isCollapsed && (
            <div className={isBubbleOrOpenCTA ? classes.cwWidgetBodyOpenCta : classes.cwWidgetBodySemiOpenCta}>
              <div
                className={classes.cwWidgetCloseBtn}
                onClick={onClose}
              >
                <CloseXIcon />
              </div>
              {isBubbleOrOpenCTA && (
                <div className={classes.cwWidgetOpenCtaHeader}>
                  <div className={classes.cwWidgetTitleWrapper}>
                    <span
                      className={!isChatEnabled ? classes.cwOfflineText : ''}
                      style={{ fontFamily: theme?.font }}
                    >
                      {
                        isChatEnabled
                          ? texts?.welcomeText
                            ? texts?.welcomeText
                            : t('chatBubble.hi_how_can_i_help_you') // Text: Hi, How can I help you?
                          : texts?.offlineWelcomeText
                          ? texts?.offlineWelcomeText
                          : t('chatBubble.we_are_offline') // Text: We are offline
                      }
                    </span>
                  </div>
                </div>
              )}

              <div
                className={classes.cwWidgetBtnWrapper}
                style={{ alignItems: type === WidgetType.SEMI_OPEN_CTA ? 'flex-end' : 'baseline' }}
              >
                {isChatEnabled ? (
                  <>
                    <button
                      id="conferwith-start-video-call"
                      aria-label={`Video button`}
                      onClick={() => onBubbleButtonClick(true)}
                      style={{
                        ...customStyle,
                        background: color,
                        borderColor: color,
                        color: primaryTextColor
                      }}
                      className={classes.cwPrimaryBtn}
                    >
                      {renderPrimaryIconBaseOnBaseUrl({ primaryTextColor, styles: { marginRight: '10px' } })}
                      {widgetOptionType && (
                        <span
                          className={classes.cwBtnText}
                          style={{
                            fontFamily: theme?.font,
                            fontWeight: buttonShape === ButtonShape.SQUARE ? 600 : 700
                          }}
                        >
                          {texts?.startCallText ? texts?.startCallText : 'Live video'}
                          {isActiveLastSession ? '(Rejoin)' : ''}
                        </span>
                      )}
                    </button>

                    {renderOtherWidget()}

                    {schedulingOption.enabled && (
                      <button
                        aria-label={`Book button`}
                        id="conferwith-book-appointment"
                        style={{ ...customStyle, border: `2px solid ${color}`, color: textColor }}
                        onClick={() => onAppointmentClick()}
                        className={classes.cwSecondaryBtn}
                      >
                        <CalenderIcon
                          style={{ marginRight: '10px' }}
                          color={textColor}
                        />
                        {widgetOptionType && (
                          <span
                            className={classes.cwBtnText}
                            style={{
                              fontFamily: theme?.font,
                              fontWeight: fontWeightValue
                            }}
                          >
                            {/* Text: APPOINTMENT */}
                            {texts?.bookAppointmentText ? texts?.bookAppointmentText : t('chatBubble.appointment')}
                          </span>
                        )}
                      </button>
                    )}

                    {onlineMenu &&
                      onlineMenu.map((data: MenuOption, i: React.Key | null | undefined) => {
                        const { externalFunction, link, label } = getMenuItems(data, onlineBtn);
                        return (
                          <button
                            aria-label={`${label} button`}
                            key={i}
                            style={{ border: `2px solid ${color}`, color: textColor, ...customStyle }}
                            className={classes.cwSecondaryBtn}
                            onClick={() => {
                              if (!link && externalFunction) {
                                externalFunction();
                                if (data.data.type === MenuType.JSON) {
                                  setOnlineBtn(!onlineBtn);
                                }
                              } else {
                                window.location.href = link || '';
                              }
                            }}
                          >
                            <i
                              className={`${data.data.icon} ${classes.cwButtonIcons}`}
                              style={{ fontSize: '12px' }}
                            />
                            {widgetOptionType && (
                              <span
                                className={classes.cwBtnText}
                                style={{
                                  fontFamily: theme?.font,
                                  fontWeight: fontWeightValue
                                }}
                              >
                                {label}
                              </span>
                            )}
                          </button>
                        );
                      })}
                  </>
                ) : (
                  <>
                    <button
                      id="conferwith-start-video-call"
                      aria-label={`Video button`}
                      onClick={() => onBubbleButtonClick(true)}
                      style={{
                        background: color,
                        color: primaryTextColor,
                        display: isActiveLastSession ? 'flex' : 'none',
                        ...customStyle
                      }}
                      className={classes.cwPrimaryBtn}
                    >
                      <VideoIcon
                        style={{ marginRight: '10px' }}
                        color={primaryTextColor}
                      />
                      {widgetOptionType && (
                        <span
                          className={classes.cwBtnText}
                          style={{
                            fontFamily: theme?.font,
                            fontWeight: buttonShape === ButtonShape.SQUARE ? 600 : 700
                          }}
                        >
                          {texts?.startCallText ? texts?.startCallText : 'Live video'}
                          {isActiveLastSession ? ' (Rejoin)' : ''}
                        </span>
                      )}
                    </button>

                    {renderOtherWidget()}

                    {offlineMenu &&
                      offlineMenu.map((data: MenuOption, i: React.Key) => {
                        const { externalFunction, link, label } = getMenuItems(data, offlineBtn);
                        return (
                          <button
                            aria-label={`${label} button`}
                            key={i}
                            style={{ border: `2px solid ${color}`, color: textColor, ...customStyle }}
                            className={classes.cwSecondaryBtn}
                            onClick={() => {
                              onPressOfflineLink();
                              if (!link && externalFunction) {
                                externalFunction();
                                if (data.data.type === MenuType.JSON) {
                                  setOfflineBtn(!offlineBtn);
                                }
                              } else {
                                window.location.href = link || '';
                              }
                            }}
                          >
                            <i
                              className={`${data.data.icon} ${classes.cwButtonIcons}`}
                              style={{ fontSize: '12px' }}
                            />
                            {widgetOptionType && (
                              <span
                                className={classes.cwBtnText}
                                style={{
                                  fontFamily: theme?.font,
                                  fontWeight: fontWeightValue
                                }}
                              >
                                {label}
                              </span>
                            )}
                          </button>
                        );
                      })}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default ChatBubble;
